/** Read-only comparison. Normal mode scrolls with the workspace; fullscreen owns its scrollport. */
import { comparisonFieldLabel } from "@kollectivia/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { cn } from "../../lib/cn";
import { comparisonContext, comparisonSourcePath } from "../../lib/comparisonNavigation";
import { useProjectComparison } from "../../lib/queries/comparison";
import { useComparisonNotes } from "../../lib/queries/comparison-notes";
import { useInsurerInformationRequests } from "../../lib/queries/insurerInformationRequests";
import { scrollWorkspaceTarget } from "../../lib/scrollWorkspaceTarget";
import { PreparationCabinetGate } from "../quote-requests/_parts/PreparationCabinetGate";

import { CellStatusBadge, HospitalRoomLevelBadge } from "./_parts/CellStatusBadge";
import { ComparisonCyclePicker } from "./_parts/ComparisonCyclePicker";
import { ComparisonExports } from "./_parts/ComparisonExports";
import { toComparisonLocale } from "./_parts/comparisonFormat";
import { ComparisonNoteIndicator } from "./_parts/ComparisonNoteIndicator";
import { ComparisonNotesDialog, type ComparisonNotesScope } from "./_parts/ComparisonNotesDialog";
import { ComparisonValue } from "./_parts/ComparisonValue";
import { ConsensusIndicator } from "./_parts/ConsensusIndicator";
import { InsurerReceptionBar } from "./_parts/InsurerReceptionBar";
import { RequestInformationCell } from "./_parts/RequestInformationCell";
import { ReviewReasonHint } from "./_parts/ReviewReasonHint";

export function ProjectComparisonPage() {
  return (
    <PreparationCabinetGate>
      <ScopedProjectComparisonPage />
    </PreparationCabinetGate>
  );
}

function ScopedProjectComparisonPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  // 9.S.1 — locale stricte CA (broker Québec) + tooltip prefix i18n.
  const comparisonLocale = toComparisonLocale(i18n.resolvedLanguage);
  const tooltipPrefix = t("comparison.format.tooltip_original_value");
  const { id } = useParams<{ id: string }>();

  const [params, setParams] = useSearchParams();
  const context = comparisonContext(params);
  const quoteRequestId = new URLSearchParams(context).get("quote_request_id") ?? undefined;
  const { data, isLoading, error, refetch } = useProjectComparison({
    projectId: id,
    quoteRequestId,
  });

  const restored = useRef("");
  useEffect(() => {
    const field = new URLSearchParams(context).get("field");
    const key = `${id}:${context}`;
    if (!field || !data || restored.current === key) return;
    const row = Array.from(document.querySelectorAll<HTMLElement>("[data-comparison-field]")).find(
      (node) => node.dataset.comparisonField === field && node.getBoundingClientRect().width > 0,
    );
    if (!row) return;
    const frame = requestAnimationFrame(() => {
      // Skip the horizontal table scrollport: only the workspace owns vertical scrolling.
      const target = row.closest<HTMLElement>("[data-comparison-matrix]") ?? row;
      const toolbar = document.querySelector<HTMLElement>("[data-comparison-toolbar]");
      const offset = (toolbar?.offsetHeight ?? 0) + (window.innerWidth < 1024 ? 64 : 0) + 8;
      scrollWorkspaceTarget(
        target,
        offset - (row.getBoundingClientRect().top - target.getBoundingClientRect().top),
      );
      row.focus({ preventScroll: true });
      restored.current = key;
    });
    return () => cancelAnimationFrame(frame);
  }, [context, data, id]);

  // Soumissions ayant ≥1 valeur extraite (apparaissent dans au moins une row).
  const subsWithValues = useMemo(() => {
    const set = new Set<string>();
    if (data) {
      for (const row of data.rows) {
        for (const sid of Object.keys(row.cells)) set.add(sid);
      }
    }
    return set;
  }, [data]);

  // 9.Q.2 — Comparison Notes MVP : fetch toutes les notes du projet (un seul
  // GET, < 50 notes/projet attendu Phase 1). Groupage côté client par scope
  // pour décider de l'affichage des indicateurs sur row/cell.
  const notesQ = useComparisonNotes({ projectId: id });
  // 9.AN.1 — les demandes d'information du dossier, indexées par (destinataire,
  // champ). Une seule lecture pour toute la matrice : une requête par cellule
  // serait insoutenable sur un dossier à cinq assureurs.
  const { data: demandes } = useInsurerInformationRequests(id);
  const demandeParCle = useMemo(() => {
    const m = new Map<
      string,
      { status: string; last_sent_at: string | null; sent_to: string | null }
    >();
    // `Array.isArray` et pas `?? []` : le typage dit que c'est un tableau, la
    // réalité ne le garantit pas — réponse en cache d'un autre contrat, proxy qui
    // réécrit, serveur non redéployé. Une valeur non itérable faisait planter la
    // matrice ENTIÈRE pour un indicateur secondaire. Même garde que
    // `CellStatusBadge` sur un `cell_status` inconnu, et pour la même raison.
    for (const d of Array.isArray(demandes) ? demandes : []) {
      m.set(`${d.scope_recipient_id}|${d.field_path}`, {
        status: d.status,
        last_sent_at: d.last_sent_at,
        sent_to: d.sent_to,
      });
    }
    return m;
  }, [demandes]);

  // Le destinataire assureur de chaque proposition — jamais déduit d'un code.
  const recipientParSubmission = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of data?.insurers ?? []) {
      if (i.submission_id) m.set(i.submission_id, i.recipient_id);
    }
    return m;
  }, [data]);

  const noteCounts = useMemo(() => {
    const project: number = (notesQ.data?.items ?? []).filter(
      (n) => n.field_key === null && n.submission_id === null,
    ).length;
    const fieldByKey = new Map<string, number>();
    const cellBySubField = new Map<string, number>();
    for (const n of notesQ.data?.items ?? []) {
      if (n.field_key !== null && n.submission_id === null) {
        fieldByKey.set(n.field_key, (fieldByKey.get(n.field_key) ?? 0) + 1);
      } else if (n.field_key !== null && n.submission_id !== null) {
        const k = `${n.submission_id}:${n.field_key}`;
        cellBySubField.set(k, (cellBySubField.get(k) ?? 0) + 1);
      }
    }
    return { project, fieldByKey, cellBySubField };
  }, [notesQ.data]);

  // Dialog state — `null` = closed. Set à un scope = open + scope-aware.
  const [openScope, setOpenScope] = useState<ComparisonNotesScope | null>(null);
  const closeNotesDialog = useCallback(() => setOpenScope(null), []);

  // 9.Q.1 polish — mode maximize (workbench plein écran). Desktop/tablet only.
  // Mobile (<md) reste sur les cards stack (déjà optimisées, pas de gain à
  // maximiser un layout vertical natif).
  const [isMaximized, setIsMaximized] = useState(false);
  /**
   * 9.AB.1 — bascule des lignes masquées.
   *
   * Fermée par défaut : la vue comparative met en avant les garanties, et sort de
   * la vue les métadonnées du document, les débris techniques et les lignes vides
   * chez tous les assureurs — 15 des 43 lignes du projet de smoke.
   *
   * Mais le masquage doit rester **réversible, visible et non destructif** (décision
   * PO). D'où un bouton qui annonce le nombre exact, et une explication du motif sur
   * chaque ligne réapparue. Aucune donnée n'est retirée de la charge : tout est là,
   * simplement replié.
   */
  const showHiddenRows = params.get("show_hidden") === "1";
  const toggleHiddenRows = () =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (showHiddenRows) next.delete("show_hidden");
      else next.set("show_hidden", "1");
      return next;
    });

  /**
   * Lignes réellement rendues. Le filtre est calculé UNE fois et consommé par les
   * deux rendus (tableau et cartes) : deux filtrages séparés finiraient par
   * divergerc, et l'utilisateur verrait deux matrices différentes selon la largeur
   * de son écran.
   *
   * `hidden_by_default` et `hidden_reason` viennent du SERVEUR — aucune liste de
   * champs n'est recopiée ici.
   */
  const visibleRows = useMemo(() => {
    const rows = data?.rows ?? [];
    return showHiddenRows ? rows : rows.filter((r) => !r.hidden_by_default);
  }, [data?.rows, showHiddenRows]);
  const closeMaximized = useCallback(() => setIsMaximized(false), []);
  useEffect(() => {
    if (!isMaximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMaximized();
      }
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll pendant le maximize.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isMaximized, closeMaximized]);

  /**
   * Libellé métier d'un field_key. Depuis 9.AU.1 le dictionnaire vit dans
   * `@kollectivia/shared` : le conseiller et l'assureur doivent lire le MÊME mot,
   * or le courriel est rendu par le worker, qui ne peut pas atteindre l'i18n web.
   * Rendu inchangé — dictionnaire d'abord, repli humanisé ensuite. La clé brute
   * reste affichée en sous-titre mono `translate="no"` pour la traçabilité.
   */
  // La langue du LIBELLÉ suit `i18n.language`, la même source que `t()`, et non le
  // `lang` de la ligne 142 : les deux divergent réellement dans cette page — un
  // `resolvedLanguage` absent retombe sur « fr » alors que l'interface est en
  // anglais. Écart préexistant, consigné en dette (il affecte aussi les messages
  // d'erreur d'API rendus ici) ; on ne le corrige pas au passage.
  const langueLibelle = i18n.language?.toLowerCase().startsWith("en") ? "en" : "fr";
  const fieldLabel = useCallback(
    (key: string): string => comparisonFieldLabel(key, langueLibelle),
    [langueLibelle],
  );

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-none",
        // Maximize : prend tout le viewport, son propre contexte scroll →
        // sticky thead + sticky left col sans conflit avec page header / AppShell.
        isMaximized && "fixed inset-0 z-50 flex flex-col bg-white",
      )}
      {...(isMaximized ? { role: "region", "aria-label": t("comparison.maximized_label") } : {})}
    >
      {/* Page header.
          Normal mode : bloc en flow naturel — pas de sticky/bg/border (la
          « barre blanche » des itérations précédentes venait du chrome sticky
          page header sur fond slate-50/95 ; le titre seul suffit, pas besoin
          d'une bande permanente sous la topbar AppShell).
          Maximize : workbench bar shrink-0 distincte avec bg-slate-50 +
          border-b pour séparer visuellement de la table. */}
      <header
        className={cn(
          !isMaximized
            ? "py-3"
            : "shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-3 lg:px-6",
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {!isMaximized && (
              <nav aria-label={t("common.breadcrumb")} className="mb-1.5 text-sm">
                <Link
                  to={`/projects/${id}`}
                  className="focus-visible:ring-brand-500 touch-manipulation rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  ← {t("comparison.back_to_project")}
                </Link>
              </nav>
            )}
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 lg:text-2xl">
              {t("comparison.title")}
            </h1>
            <p className="mt-0.5 text-xs text-slate-600 lg:text-sm">{t("comparison.subtitle")}</p>
            <p className="mt-2 text-sm font-medium text-slate-700">
              {t(quoteRequestId ? "reception_flow.cycle" : "reception_flow.all_cycles")}
            </p>
            {quoteRequestId && (
              <div className="mt-1 flex flex-wrap gap-3 text-sm">
                <Link className="underline" to={`/quote-requests/${quoteRequestId}/parcours`}>
                  {t("reception_flow.request")}
                </Link>
                <Link className="underline" to={`/projects/${id}/comparison`}>
                  {t("reception_flow.all_cycles")}
                </Link>
              </div>
            )}
          </div>

          {id && <ComparisonCyclePicker key={id} projectId={id} />}
        </div>
      </header>
      {id && data && (
        <ComparisonExports
          key={`${id}:${quoteRequestId ?? ""}:${i18n.resolvedLanguage}`}
          projectId={id}
          quoteRequestId={quoteRequestId}
          charge={data}
        />
      )}

      <div
        data-comparison-toolbar
        className={cn(
          "flex shrink-0 flex-wrap items-center gap-2 py-3",
          isMaximized ? "border-b bg-white px-4 lg:px-6" : "workspace-command-bar",
        )}
      >
        <button
          type="button"
          onClick={() => setOpenScope({ kind: "project" })}
          className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M2 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5l-2 2V3Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
          {t("comparison_notes.project_button")}
          {noteCounts.project > 0 && (
            <span className="ml-0.5 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold tabular-nums text-amber-800">
              {noteCounts.project}
            </span>
          )}
        </button>
        {/* 9.AI.1 — la sortie vers le rapport. Le comparateur est l'écran de
                travail ; le rapport est ce qu'on relit et qu'on remet. Un lien,
                pas un bouton : il navigue, et Cmd-clic doit marcher. */}
        {/* 9.AJ.1 — le classement pondéré. Un lien : il navigue. */}
        {!isMaximized ? (
          <Link
            to={`/projects/${id}/scoring${context}`}
            className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("scoring.cta")}
          </Link>
        ) : null}
        {!isMaximized ? (
          <Link
            to={`/projects/${id}/report${context}`}
            className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("report.cta")}
          </Link>
        ) : null}
        {!isMaximized ? (
          <button
            type="button"
            onClick={() => setIsMaximized(true)}
            className="focus-visible:ring-brand-500 hidden touch-manipulation items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 md:inline-flex"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 6V3h3M13 6V3h-3M3 10v3h3M13 10v3h-3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("comparison.maximize")}
          </button>
        ) : (
          <button
            type="button"
            onClick={closeMaximized}
            aria-label={t("comparison.close_maximized")}
            className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
            {t("common.close")}
          </button>
        )}
      </div>

      <div
        className={cn(
          !isMaximized ? "px-0 py-4 lg:py-6" : "flex-1 overflow-hidden px-3 py-3 lg:px-6 lg:py-4",
        )}
      >
        {/* Error */}
        {error && (
          <div
            role="alert"
            className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <span>
              {error instanceof ApiException
                ? getApiErrorMessage(error, lang) || t("errors.generic")
                : t("errors.generic")}
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="touch-manipulation rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {t("common.retry")}
            </button>
          </div>
        )}

        {/* Loading */}
        {isLoading && !data && (
          <div
            aria-live="polite"
            className="space-y-2 rounded-xl border border-slate-200 bg-white p-4"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded-md bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none"
              />
            ))}
          </div>
        )}

        {/* Chunk 9.AH.1 — la RÉCEPTION, au-dessus de la matrice et jamais dedans.
            Placée avant l'état vide : c'est précisément quand il n'y a rien à
            comparer qu'il faut dire « pas encore » plutôt que « rien ». */}
        {data && <InsurerReceptionBar data={data} />}

        {data && data.rows.length === 0 && data.submissions.length > 0 && (
          <section className="mb-4 rounded-xl border bg-white p-4">
            <h2 className="font-medium">{t("reception_flow.source")}</h2>
            <ul className="mt-2 space-y-2">
              {data.submissions.map((submission) => (
                <li key={submission.id}>
                  <Link
                    className="text-sm underline"
                    to={comparisonSourcePath(submission.id, params)}
                  >
                    {submission.insurer_name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Empty — aucune soumission */}
        {data && data.submissions.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
            <h2 className="text-base font-semibold text-slate-900">
              {t(quoteRequestId ? "reception_flow.empty_cycle_title" : "comparison.empty.title")}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              {t(quoteRequestId ? "reception_flow.empty_cycle_body" : "comparison.empty.subtitle")}
            </p>
            {quoteRequestId && (
              <Link
                className="mt-3 block text-sm font-medium underline"
                to={`/projects/${id}/comparison`}
              >
                {t("reception_flow.all_cycles")}
              </Link>
            )}
            <Link
              to={`/projects/${id}`}
              className="focus-visible:ring-brand-500 mt-4 inline-flex touch-manipulation items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("comparison.back_to_project")}
            </Link>
          </div>
        )}

        {/* Matrice — ≥1 submission */}
        {data && data.submissions.length > 0 && (
          <>
            {/* Récap compact : combien de soumissions ont des valeurs extraites
                (caché en maximize — la barre est dédiée à la matrice). */}
            {!isMaximized && (
              <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 lg:px-4 lg:text-sm">
                {t("comparison.summary", {
                  withValues: subsWithValues.size,
                  total: data.submissions.length,
                })}
              </div>
            )}

            {/* Help banner si < 2 assureurs (caché en maximize). */}
            {!isMaximized && data.submissions.length < 2 && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {t("comparison.help.single_submission")}
              </div>
            )}

            {/* Aucune donnée extraite */}
            {data.rows.length === 0 ? (
              !isMaximized && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
                  <h2 className="text-base font-semibold text-slate-900">
                    {t("comparison.no_data.title")}
                  </h2>
                  <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
                    {t("comparison.no_data.subtitle")}
                  </p>
                </div>
              )
            ) : (
              <>
                {/* 9.AB.1 — bandeau des passes sans résultat exploitable.
                    Décision PO option B : une passe qui échoue AVANT de produire des
                    champs n'a aucune cellule dans la matrice. On la signale ici,
                    plutôt que de fabriquer des lignes fantômes qui n'existeraient
                    que parce qu'une extraction a échoué.
                    Placé tout en haut : le conseiller doit savoir qu'une garantie
                    n'a PAS été lue avant de tirer la moindre conclusion. */}
                {data.submissions.some((s) => s.failed_passes.length > 0) && (
                  <div
                    role="status"
                    className="mb-3 rounded-md border border-red-200 bg-red-50 p-3"
                  >
                    <p className="text-xs font-medium text-red-800">
                      {t("comparison.failed_passes.title")}
                    </p>
                    <p className="mt-1 max-w-prose text-sm text-red-800">
                      {t("comparison.failed_passes.body")}
                    </p>
                    <ul className="mt-2 space-y-0.5">
                      {data.submissions
                        .filter((s) => s.failed_passes.length > 0)
                        .map((s) => (
                          <li key={s.id} className="text-xs text-red-900">
                            {t("comparison.failed_passes.for_insurer", {
                              insurer: s.insurer_name,
                              // Libellé métier, jamais l'identifiant technique : le
                              // conseiller lit « Soins dentaires », pas `dentaire`.
                              passes: s.failed_passes
                                .map((p) =>
                                  t(`submissions.detail.orchestration.pass_label.${p.pass_id}`, {
                                    defaultValue: p.pass_id,
                                  }),
                                )
                                .join(", "),
                            })}
                          </li>
                        ))}
                    </ul>
                    <p className="mt-2 text-xs text-red-700">
                      {t("comparison.failed_passes.relaunch_hint")}
                    </p>
                  </div>
                )}

                {/* 9.AB.1 — bascule des lignes masquées, AU-DESSUS des deux rendus.
                    Placée là volontairement : l'utilisateur doit savoir qu'il ne
                    voit pas tout AVANT de lire la matrice, pas après. Le compte
                    vient du serveur (`hidden_row_count`), le motif de chaque ligne
                    aussi — rien n'est recompté ici. */}
                {data.hidden_row_count > 0 && (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={toggleHiddenRows}
                      aria-expanded={showHiddenRows}
                      className="focus-visible:ring-brand-500 touch-manipulation rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
                    >
                      {showHiddenRows
                        ? t("comparison.hidden_rows.hide")
                        : t("comparison.hidden_rows.show", { count: data.hidden_row_count })}
                    </button>
                    <p className="text-xs text-slate-500">
                      {t("comparison.hidden_rows.explanation")}
                    </p>
                  </div>
                )}

                {/* === Desktop ≥md : matrice 2D comparative (cœur produit) === */}
                <div className={cn("hidden md:block", isMaximized && "h-full")}>
                  {/* Hint scroll horizontal — visible md uniquement, caché en maximize. */}
                  {!isMaximized && (
                    <p className="mb-2 text-xs text-slate-400 lg:hidden">
                      {t("comparison.scroll_hint")}
                    </p>
                  )}
                  <div className={cn("relative", isMaximized && "h-full")}>
                    <div
                      data-comparison-matrix
                      className={cn(
                        "border-slate-200 bg-white",
                        !isMaximized
                          ? "isolate overflow-x-auto overscroll-x-contain rounded-xl border"
                          : "h-full overflow-auto overscroll-contain rounded-xl border",
                      )}
                    >
                      <table className="min-w-full border-collapse text-sm">
                        <thead
                          className={cn(
                            "bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500",
                            // Only fullscreen owns a vertically bounded table scrollport.
                            isMaximized ? "sticky top-0 z-20" : "",
                          )}
                        >
                          <tr>
                            <th
                              scope="col"
                              // Corner cell : sticky left-0 + align-top (sinon
                              // « CHAMP » est centré verticalement dans une row
                              // haute — quand un cell assureur a name+code+hint+
                              // badge — créant des bandes blanches autour).
                              // z-20 > body sticky-left z-10 → corner gagne à
                              // l'intersection top-left en mode maximize ; en
                              // normal le z-20 reste cohérent (intersection horiz
                              // seulement, pas de top-sticky).
                              className="sticky left-0 z-20 min-w-[8rem] bg-slate-50 px-4 py-3 align-top font-medium sm:min-w-[11rem] lg:min-w-[14rem]"
                            >
                              {t("comparison.columns.field")}
                            </th>
                            {data.submissions.map((s) => (
                              <th
                                key={s.id}
                                scope="col"
                                className="min-w-[10rem] px-4 py-3 align-top font-medium"
                              >
                                <Link
                                  to={comparisonSourcePath(s.id, params)}
                                  className="block break-words text-slate-900 underline underline-offset-2"
                                >
                                  {s.insurer_name}
                                </Link>
                                {/* code assureur = identifiant → pas d'auto-traduction */}
                                <span
                                  className="block break-words font-mono text-[10px] normal-case text-slate-400"
                                  translate="no"
                                >
                                  {s.insurer_code}
                                </span>
                                {!subsWithValues.has(s.id) && (
                                  <span className="mt-0.5 block text-[10px] font-normal normal-case text-slate-400">
                                    {t("comparison.no_extracted_values")}
                                  </span>
                                )}
                                {s.open_anomaly_count > 0 && (
                                  <span className="mt-0.5 inline-flex items-center rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-inset ring-red-200">
                                    {t("comparison.anomaly_count", { count: s.open_anomaly_count })}
                                  </span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {visibleRows.map((row) => (
                            <tr
                              key={row.field_key}
                              data-comparison-field={row.field_key}
                              tabIndex={-1}
                              // Striping opaque (pas /60) — la sticky-left body
                              // `bg-inherit` doit être opaque sinon le contenu
                              // glissant horizontalement transparaît à travers
                              // la 1ʳᵉ colonne.
                              className="align-top odd:bg-white even:bg-slate-50 hover:bg-slate-100"
                            >
                              <th
                                scope="row"
                                className="sticky left-0 z-10 min-w-[8rem] max-w-[16rem] bg-inherit px-4 py-3 text-left align-top sm:min-w-[11rem] lg:min-w-[14rem]"
                              >
                                <div className="flex items-start gap-1">
                                  <div className="min-w-0 flex-1">
                                    <span className="block break-words text-xs font-semibold text-slate-800">
                                      {fieldLabel(row.field_key)}
                                    </span>
                                    {/* field_key canonique = identifiant → pas d'auto-traduction */}
                                    <span
                                      className="block break-words font-mono text-[10px] text-slate-400"
                                      translate="no"
                                    >
                                      {row.field_key}
                                    </span>
                                    {/* 9.AB.1 — une ligne réapparue dit POURQUOI elle
                                        était masquée. Sans ce motif, la bascule
                                        montrerait des lignes surgies de nulle part. */}
                                    {row.hidden_by_default && row.hidden_reason && (
                                      <span className="mt-0.5 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-normal normal-case text-slate-500">
                                        {t(`comparison.hidden_rows.reason.${row.hidden_reason}`)}
                                      </span>
                                    )}
                                  </div>
                                  {/* 9.Q.2 indicateur notes field-scope */}
                                  <ComparisonNoteIndicator
                                    variant="row"
                                    count={noteCounts.fieldByKey.get(row.field_key) ?? 0}
                                    onOpen={() =>
                                      setOpenScope({
                                        kind: "field",
                                        field_key: row.field_key,
                                        field_label: fieldLabel(row.field_key),
                                      })
                                    }
                                  />
                                </div>
                              </th>
                              {data.submissions.map((s) => {
                                const cell = row.cells[s.id];
                                const cellNoteCount =
                                  noteCounts.cellBySubField.get(`${s.id}:${row.field_key}`) ?? 0;
                                if (!cell) {
                                  return (
                                    <td key={s.id} className="px-4 py-3 align-top text-slate-300">
                                      <div className="flex items-start justify-between gap-1">
                                        <span>—</span>
                                        <ComparisonNoteIndicator
                                          variant="cell"
                                          count={cellNoteCount}
                                          onOpen={() =>
                                            setOpenScope({
                                              kind: "cell",
                                              field_key: row.field_key,
                                              field_label: fieldLabel(row.field_key),
                                              submission_id: s.id,
                                              insurer_name: s.insurer_name,
                                            })
                                          }
                                        />
                                      </div>
                                    </td>
                                  );
                                }
                                return (
                                  <td
                                    key={s.id}
                                    className="px-4 py-3 align-top text-sm text-slate-800"
                                  >
                                    <div className="flex items-start justify-between gap-1">
                                      <div className="flex min-w-0 flex-col gap-1">
                                        <ComparisonValue
                                          value={cell.value}
                                          unit={cell.unit}
                                          fieldKey={row.field_key}
                                          locale={comparisonLocale}
                                          tooltipPrefix={tooltipPrefix}
                                        />
                                        <Link
                                          className="text-xs underline"
                                          to={comparisonSourcePath(s.id, params, row.field_key)}
                                        >
                                          {t("reception_flow.source")}
                                        </Link>
                                        {/* 9.AB.1 — `cell_status` est le signal
                                            PRINCIPAL. L'ancien drapeau
                                            `needs_review` reste dans la charge wire
                                            pour ses consommateurs, mais il n'est
                                            plus rendu ici : il était posé sur 36 %
                                            des cellules, dont 28 simplement nulles.
                                            Le statut porte l'information juste,
                                            sans le doublon. */}
                                        <span className="flex flex-wrap gap-1">
                                          <CellStatusBadge cell={cell} />
                                          <HospitalRoomLevelBadge cell={cell} />
                                          {/* 9.AK.1 — sur QUOI la valeur repose.
                                              Distinct de la sémantique de cellule
                                              et du drapeau de revue. */}
                                          <ConsensusIndicator consensus={cell.consensus} />
                                          {/* 9.AL.1 — POURQUOI la revue est demandée. */}
                                          {cell.needs_review ? (
                                            <ReviewReasonHint reasons={cell.needs_review_reasons} />
                                          ) : null}
                                          {/* 9.AN.1 — demander l'information, ou son état. */}
                                          <RequestInformationCell
                                            cellStatus={cell.cell_status}
                                            recipientId={recipientParSubmission.get(s.id)}
                                            fieldPath={row.field_key}
                                            demande={demandeParCle.get(
                                              `${recipientParSubmission.get(s.id)}|${row.field_key}`,
                                            )}
                                          />
                                        </span>
                                        {cell.has_open_anomaly && (
                                          <span className="flex flex-wrap gap-1">
                                            <span
                                              title={t("comparison.flags.anomaly")}
                                              className="inline-flex items-center rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-inset ring-red-200"
                                            >
                                              {t("comparison.flags.anomaly_short")}
                                            </span>
                                          </span>
                                        )}
                                      </div>
                                      {/* 9.Q.2 indicateur notes cell-scope */}
                                      <ComparisonNoteIndicator
                                        variant="cell"
                                        count={cellNoteCount}
                                        onOpen={() =>
                                          setOpenScope({
                                            kind: "cell",
                                            field_key: row.field_key,
                                            field_label: fieldLabel(row.field_key),
                                            submission_id: s.id,
                                            insurer_name: s.insurer_name,
                                          })
                                        }
                                      />
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Gradient hint scroll droite — signale qu'il y a plus à droite.
                        Subtil, pointer-events-none. Caché en maximize (l'overlay
                        a sa propre UX scroll claire). */}
                    {!isMaximized && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 right-0 w-8 rounded-r-xl bg-gradient-to-l from-white via-white/70 to-transparent"
                      />
                    )}
                  </div>
                </div>

                {/* === Mobile <md : cards stack — 1 card / field_key ===
                    Comparaison cross-row par scroll vertical naturel.
                    Cachée en maximize (maximize = desktop/tablet only). */}
                <ul className={cn("space-y-3 md:hidden", isMaximized && "hidden")}>
                  {visibleRows.map((row) => (
                    <li
                      key={row.field_key}
                      data-comparison-field={row.field_key}
                      tabIndex={-1}
                      className="rounded-xl border border-slate-200 bg-white p-4"
                    >
                      <div className="flex items-start gap-2 border-b border-slate-100 pb-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="break-words text-sm font-semibold text-slate-900">
                            {fieldLabel(row.field_key)}
                          </h3>
                          <span
                            className="block break-words font-mono text-[10px] text-slate-400"
                            translate="no"
                          >
                            {row.field_key}
                          </span>
                        </div>
                        {/* 9.Q.2 indicateur notes field-scope (mobile cards) */}
                        <ComparisonNoteIndicator
                          variant="card"
                          count={noteCounts.fieldByKey.get(row.field_key) ?? 0}
                          onOpen={() =>
                            setOpenScope({
                              kind: "field",
                              field_key: row.field_key,
                              field_label: fieldLabel(row.field_key),
                            })
                          }
                        />
                      </div>
                      <dl className="mt-2 divide-y divide-slate-100">
                        {data.submissions.map((s) => {
                          const cell = row.cells[s.id];
                          const cellNoteCount =
                            noteCounts.cellBySubField.get(`${s.id}:${row.field_key}`) ?? 0;
                          return (
                            <div
                              key={s.id}
                              className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
                            >
                              <dt className="min-w-0 flex-1">
                                <Link
                                  to={comparisonSourcePath(s.id, params, row.field_key)}
                                  className="block break-words text-xs font-medium text-slate-800 underline underline-offset-2"
                                >
                                  {s.insurer_name}
                                </Link>
                                <span
                                  className="block break-words font-mono text-[10px] text-slate-400"
                                  translate="no"
                                >
                                  {s.insurer_code}
                                </span>
                              </dt>
                              <dd className="min-w-0 text-right text-sm text-slate-800">
                                {cell ? (
                                  <div className="flex flex-col items-end gap-1">
                                    <ComparisonValue
                                      value={cell.value}
                                      unit={cell.unit}
                                      fieldKey={row.field_key}
                                      locale={comparisonLocale}
                                      tooltipPrefix={tooltipPrefix}
                                    />
                                    {/* 9.AB.1 — même signal principal sur le rendu
                                        mobile : une divergence entre les deux vues
                                        créerait deux vérités. */}
                                    <span className="flex flex-wrap justify-end gap-1">
                                      <CellStatusBadge cell={cell} />
                                      <HospitalRoomLevelBadge cell={cell} />
                                      <ConsensusIndicator consensus={cell.consensus} />
                                      {/* 9.AL.1 — POURQUOI la revue est demandée. */}
                                      {cell.needs_review ? (
                                        <ReviewReasonHint reasons={cell.needs_review_reasons} />
                                      ) : null}
                                      <RequestInformationCell
                                        cellStatus={cell.cell_status}
                                        recipientId={recipientParSubmission.get(s.id)}
                                        fieldPath={row.field_key}
                                        demande={demandeParCle.get(
                                          `${recipientParSubmission.get(s.id)}|${row.field_key}`,
                                        )}
                                      />
                                    </span>
                                    {cell.has_open_anomaly && (
                                      <span className="flex flex-wrap justify-end gap-1">
                                        <span
                                          title={t("comparison.flags.anomaly")}
                                          className="inline-flex items-center rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-inset ring-red-200"
                                        >
                                          {t("comparison.flags.anomaly_short")}
                                        </span>
                                      </span>
                                    )}
                                    {/* 9.Q.2 indicateur notes cell-scope (card) */}
                                    <ComparisonNoteIndicator
                                      variant="card"
                                      count={cellNoteCount}
                                      onOpen={() =>
                                        setOpenScope({
                                          kind: "cell",
                                          field_key: row.field_key,
                                          field_label: fieldLabel(row.field_key),
                                          submission_id: s.id,
                                          insurer_name: s.insurer_name,
                                        })
                                      }
                                    />
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-end gap-1">
                                    <span className="text-slate-300">—</span>
                                    <ComparisonNoteIndicator
                                      variant="card"
                                      count={cellNoteCount}
                                      onOpen={() =>
                                        setOpenScope({
                                          kind: "cell",
                                          field_key: row.field_key,
                                          field_label: fieldLabel(row.field_key),
                                          submission_id: s.id,
                                          insurer_name: s.insurer_name,
                                        })
                                      }
                                    />
                                  </div>
                                )}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      {/* 9.Q.2 — Dialog notes (project / field / cell scope-aware). z-[60]
          pour passer au-dessus du maximize overlay z-50. */}
      {openScope && id && (
        <ComparisonNotesDialog projectId={id} scope={openScope} onClose={closeNotesDialog} />
      )}
    </div>
  );
}
